import _ from 'underscore';
import Backbone from 'backbone';

import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { FilterService } from '@bingads-webui/filter-service';
import { SelectorFilterAgent } from './selector-filter-agent';
import { SearchboxFilterAgent } from './searchbox-filter-agent';
import { FilterEditorAgent } from './filter-editor-agent';
import { FilterBarAgent } from './filter-bar-agent';
import { FilterMenuAgent } from './filter-menu-agent';
import { localize } from './schema-localizer';

/**
 * Set up different filter UI components as configured by consumer<br>
 * Attach filter UI components to the toolbar view passed in by consumer<br>
 * Handles the interaction within various UI components as well as between
 * UI and FilterService component that manages filter state and filter storage<br>
 * Trigger filter-state-changed event and expose current combined filter in
 * the form of FilterEntity<br>
 * @extends external:"Backbone.Events"
 * @fires FilterAgent#filter-state-changed
 */

export class FilterAgent {
  /**
   * Constructor for FilterAgent - Create sub filter agents and filter service instance
   * @param {Object} options - The options object that is used to configure FilterAgent
   * @param {Object} options.toolbarView - The toolbar backbone view that Filter UI will
   * be attached to
   * @param {Object} options.i18n - I18N object for localization
   * @param {Object} options.schemaI18n - SchemaI18n object for localization
   * @param {Object} options.shortDateFormat - Format of date during localization
   * @param {Object} [options.filterService] - Preconfigure FilterService instance
   * @param {Object} [options.entitySchema] - Entity schema
   * @param {string} [options.odata] - Odata options for converting filter to query
   * @param {string} [options.storage] - Collection of storage instances to get/save filters
   * @param {string[]} option.enabled - An array of names of filter UI components
   * that are to be enabled. Value can be 'selector', 'editor' or 'searchbox'
   * @param {Object} [options.filterbar] - Configuration for FilterBar, the UI
   * component that shows the current applied filter status
   * @param {Object} [options.selector] - Configuration for Selector Filter UI.
   * Pass in if Selector Filter UI is enabled
   * @param {Object} [options.editor] - Configuration for Filter Editor UI.
   * Pass in if Filter Editor UI is enabled
   * @param {Object} [options.searchbox] - Configuration for Searchbox Filter UI.
   * Pass in if Searchbox Filter UI is enabled
   */
  constructor(options = {}) {
    this.options = options;
    if (options.filterService) {
      this.filterService = options.filterService;
    } else {
      this.filterService = new FilterService(options);
    }
    this.options.filterService = this.filterService;
    this.options.entitySchema = localize(
      this.filterService.entitySchema,
      this.options.schemaI18n,
      this.options.i18n
    );
    this.filterMenuAgent = new FilterMenuAgent(options);
    this.filterBarAgent = new FilterBarAgent(options);

    _.extend(this, Backbone.Events);
    this.setupToolbar();
  }

  /**
   * Initialze the filter UI components that are registered by consumer
   * Manage the interaction between different filter UI components
   * Set initial states for some of the filter UI components
   * @private
   * @returns {undefined}
   */
  setupToolbar() {
    if (_.contains(this.options.enabled, 'editor')) {
      this.filterEditorAgent = new FilterEditorAgent(this.options);

      this.listenTo(this.filterMenuAgent, 'create-new-filter', () => {
        this.isNewFilter = true;
        this.showFilterEditor();
      });
      this.listenTo(this.filterBarAgent, 'filter-bar-edited', () => {
        this.isNewFilter = false;
        this.showFilterEditor();
      });
      this.listenTo(this.filterEditorAgent, 'cancel-edit', () => {
        this.hideFilterEditor();
      });
      this.listenTo(this.filterEditorAgent, 'filter-saved-success', () => {
        this.filterMenuAgent.refresh();
        this.trigger('filter-saved-success');
      });
      this.listenTo(this.filterMenuAgent, 'filter-deleted-success', () => {
        this.trigger('filter-deleted-success');
      });
      this.listenTo(this.filterEditorAgent, 'filter-max-reached', () => {
        this.hideFilterEditor();
        this.trigger('filter-max-reached');
      });
    }

    // not using initialFilter, as filter may has already chanaged
    this.filterService.p$Initialized.then((/* initialFilter */) => {
      const {
        editableFilter,
        quickFilter,
      } = this.filterService;

      if (_.contains(this.options.enabled, 'selector')) {
        this.selectorFilterAgent =
        new SelectorFilterAgent(_.extend(this.options, { filter: quickFilter }));
      }
      if (!_.isEmpty(editableFilter)) {
        this.showFilterBar();
      }
    });


    if (_.contains(this.options.enabled, 'searchbox')) {
      this.searchboxFilterAgent = new SearchboxFilterAgent(this.options);
    }

    this.listenTo(this.filterService, 'editable-filter-applied', () => {
      this.showFilterBar();
    });
    this.listenTo(this.filterService, 'filter-state-changed', (combinedFilter, subStatus) => {
      if (subStatus === 'editable-filter-applied') {
        this.showFilterBar();
      }
      if (subStatus === 'editable-filter-removed') {
        if (this.searchboxFilterAgent) {
          this.searchboxFilterAgent.clear();
        }
        this.showToolBar();
      }
      this.trigger('filter-state-changed', combinedFilter, subStatus);
    }, this);

    this.listenTo(this.filterService, 'filter-operation-failed', (error) => {
      this.trigger('filter-operation-failed', error);
    }, this);

    this.filterMenuAgent.refresh();
  }

  hideFilterEditor() {
    if (this.filterService.getEditableFilter()) {
      this.showFilterBar();
    } else {
      this.showToolBar();
    }
  }

  showFilterEditor(query) {
    this.filterEditorQuery = query;
    this.trigger('filter-ui-change', {
      showFilterEditor: true,
      showToolBar: false,
      showFilterBar: false,
    });
  }

  showFilterBar() {
    if (this.filterBarAgent.deterministicView) {
      this.filterBarAgent.update(this.filterService.getEditableFilter());
    } else {
      this.trigger('filter-ui-change', {
        showToolBar: true,
        showFilterBar: true,
        showFilterEditor: false,
      });
    }
  }

  showToolBar() {
    this.trigger('filter-ui-change', {
      showToolBar: true,
      showFilterBar: false,
      showFilterEditor: false,
    });
  }

  getFilterEditor() {
    if (this.filterEditorQuery) {
      const filterToShow = this.filterService.createFilterEntity(this.filterEditorQuery);
      delete this.filterEditorQuery;
      return this.filterEditorAgent.create(filterToShow);
    }
    if (this.isNewFilter) {
      return this.filterEditorAgent.create();
    }
    const editableFilter = this.filterService.getEditableFilter();
    return editableFilter ? this.filterEditorAgent
      .create(this.filterService.createFilterEntity(
        cloneDeep(editableFilter.query),
        editableFilter.name
      )) : this.filterEditorAgent.create(editableFilter);
  }

  getFilterBar() {
    return this.filterBarAgent.create(this.filterService.getEditableFilter());
  }

  setEditableFilter(query, name) {
    this.filterService.setEditableFilter(query, name);
  }
}
